import { Component, type ReactNode } from 'react';
import { esTamperError } from '@/engine/persistence';

interface State {
  error: Error | null;
}

/**
 * Captura errores no manejados. Los `TamperDetectedError` se manejan
 * antes de que lleguen acá (en el bootstrap de stores), pero por si
 * acaso cae uno suelto, lo tratamos igual.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error) {
    // En producción se podría loggear a un servicio.
    if (!esTamperError(error)) {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error);
    }
  }

  private reintentar = () => {
    this.setState({ error: null });
  };

  override render() {
    if (this.state.error) {
      return (
        <div className="flex h-full w-full items-center justify-center p-8">
          <div className="max-w-lg text-center space-y-4">
            <h1 className="text-display text-2xl text-ember-400">
              Algo se quebró en el mundo
            </h1>
            <p className="text-narrative text-ash-200">
              Ocurrió un error inesperado. Esto no debería pasar; si persiste,
              reportalo.
            </p>
            <pre className="text-xs text-ash-500 whitespace-pre-wrap text-left bg-ash-900/60 p-3 rounded border border-ash-800">
              {this.state.error.message}
            </pre>
            <button
              type="button"
              onClick={this.reintentar}
              className="px-4 py-2 rounded bg-moss-700 hover:bg-moss-600 text-ash-50 font-ui text-sm tracking-wide uppercase"
            >
              Intentar de nuevo
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
